import { Link, Navigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle, ArrowRight, Phone, Trophy, Sparkles, Newspaper, Quote } from "lucide-react";
import { getAgencyPage, allAgencyPages } from "../data/agencyPages";
import { getSeoPage, SITE } from "../data/seoPages";

const PROCESS = [
  { t: "Free Eligibility Check", d: "Share your brand details on a call — we assess platform fit, documentation gaps and realistic timelines at zero cost." },
  { t: "Documentation Audit", d: "GST, trademark, bank and category licenses cross-verified for the name-match consistency platforms silently reject on." },
  { t: "Application & Commercials", d: "Platform applications filed through the right channels (including our Myntra OMS routing) with realistic commercial proposals." },
  { t: "AI Catalog Creation", d: "EcomCatalog.ai generates marketplace-compliant, SEO-optimized listings in 90 seconds per SKU." },
  { t: "Approval & Go-Live", d: "QC clearance, listing activation and first orders — standard timeline 14 days." },
  { t: "Growth Management", d: "Optional ongoing management: ads, analytics, catalog optimization and a dedicated growth manager." },
];

const WHY = [
  "Official OMS Partner of Myntra — direct platform access",
  "India's highest first-time approval rate: 98%",
  "1000+ brands launched on 70+ marketplaces",
  "India's first AI listing software — EcomCatalog.ai (90-second listings)",
  "Featured in The Times of India & The Economic Times",
  "Certified by BEEP, Google & Great Learning",
  "Public client results — real dashboards on our Growth Story page",
  "Dedicated account manager for every brand",
];

const STATS = [
  { v: "1000+", l: "Brands Onboarded" },
  { v: "98%", l: "First-Time Approval" },
  { v: "14 Days", l: "Standard Launch" },
  { v: "70+", l: "Marketplaces" },
];

export default function AgencyPage() {
  const slug = useLocation().pathname.replace(/^\/+|\/+$/g, "");
  const page = getAgencyPage(slug);
  if (!page) return <Navigate to="/" replace />;

  const url = `${SITE}/${page.slug}`;
  const related = page.rel.map(getAgencyPage).filter(Boolean);
  const software = (page.sw || []).map(getSeoPage).filter(Boolean);

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${SITE}/#organization`,
        name: "BrandBorn",
        url: SITE,
        telephone: "+91-9464645491",
        email: "business@brandborn.in",
        sameAs: ["https://economictimes.indiatimes.com/ai-innovator/profile/brandborn/133204259", "https://www.linkedin.com/company/brandborn1/", "https://www.instagram.com/brandborn_", "https://www.youtube.com/@brandborn1"],
      },
      {
        "@type": "LocalBusiness",
        "@id": `${SITE}/#localbusiness`,
        name: "BrandBorn — Ecommerce Onboarding Agency",
        url: SITE,
        telephone: "+91-9464645491",
        priceRange: "₹₹",
        address: { "@type": "PostalAddress", addressLocality: "Ludhiana", addressRegion: "Punjab", addressCountry: "IN" },
        areaServed: { "@type": "Country", name: "India" },
        parentOrganization: { "@id": `${SITE}/#organization` },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE },
          { "@type": "ListItem", position: 2, name: page.kw, item: url },
        ],
      },
      {
        "@type": "FAQPage",
        mainEntity: page.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      },
      {
        "@type": page.kind === "city" ? "LocalBusiness" : "Service",
        name: page.kw,
        description: page.summary,
        url,
        telephone: "+91-9464645491",
        ...(page.kind === "city"
          ? { areaServed: { "@type": "City", name: page.city }, address: { "@type": "PostalAddress", addressCountry: "IN", addressLocality: page.city }, priceRange: "₹₹" }
          : { serviceType: page.kw, areaServed: { "@type": "Country", name: "India" }, provider: { "@type": "Organization", name: "BrandBorn", url: SITE } }),
      },
      {
        "@type": "HowTo",
        name: `${page.kw} — BrandBorn's 6-step process`,
        step: PROCESS.map((s, i) => ({ "@type": "HowToStep", position: i + 1, name: s.t, text: s.d })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white pt-24" data-testid="agency-page">
      <Helmet>
        <title>{page.title}</title>
        <meta name="description" content={page.desc} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={page.title} />
        <meta property="og:description" content={page.desc} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={page.title} />
        <meta name="twitter:description" content={page.desc} />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="px-4 py-14 md:py-20 bg-gradient-to-b from-red-50 via-white to-white">
        <div className="max-w-4xl mx-auto text-center">
          <nav aria-label="Breadcrumb" className="text-xs text-gray-400 mb-4 font-semibold">
            <Link to="/" className="hover:text-red-500">Home</Link> / <span className="text-gray-600">{page.kw}</span>
          </nav>
          <p className="text-[11px] text-gray-400 font-bold mb-3" data-testid="agency-last-updated">Last updated: June 2026 • Reviewed by BrandBorn marketplace experts</p>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-black text-white rounded-full text-xs font-bold mb-5">
            <Trophy size={14} className="text-yellow-400" /> OFFICIAL MYNTRA OMS PARTNER • FEATURED IN TOI & ET
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-4 leading-tight">{page.h1}</h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">{page.sub}</p>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <a href="tel:+919464645491" className="flex items-center gap-2 px-8 py-4 bg-red-600 text-white rounded-2xl font-black text-sm uppercase tracking-wider hover:bg-red-700 hover:-translate-y-0.5 transition-all shadow-lg" data-testid="agency-call-cta">
              <Phone size={16} /> Free Eligibility Check
            </a>
            <a href="https://wa.me/919464645491?text=Hi%20BrandBorn!%20I%20want%20to%20onboard%20my%20brand." target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-green-500 text-white rounded-2xl font-black text-sm uppercase tracking-wider hover:bg-green-600 hover:-translate-y-0.5 transition-all shadow-lg" data-testid="agency-whatsapp-cta">
              WhatsApp Us
            </a>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
            {STATS.map((s, i) => (
              <div key={i} className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
                <div className="text-xl font-black text-red-600">{s.v}</div>
                <div className="text-[11px] text-gray-500 font-semibold">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Answer */}
      <section className="px-4 py-8">
        <div className="max-w-4xl mx-auto bg-gray-950 text-white rounded-3xl p-8 border border-gray-800" data-testid="agency-summary">
          <div className="flex items-center gap-2 text-xs font-bold text-blue-400 uppercase tracking-widest mb-3"><Sparkles size={14} /> Quick Answer</div>
          <p className="text-gray-200 leading-relaxed">{page.summary}</p>
        </div>
      </section>

      {/* Intro */}
      <section className="px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {page.intro.map((p, i) => (
            <p key={i} className="text-gray-600 leading-relaxed text-lg mb-5">{p}</p>
          ))}
        </div>
      </section>

      {/* Benefits */}
      <section className="px-4 py-10 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-8 text-center">Benefits of Working with BrandBorn</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            {page.benefits.map((b, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                <h3 className="font-black text-gray-900 mb-2 flex items-center gap-2"><CheckCircle size={18} className="text-green-500" />{b.t}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-8">Our 6-Step Onboarding Process</h2>
          <div className="space-y-5">
            {PROCESS.map((s, i) => (
              <div key={i} className="flex gap-4 items-start">
                <div className="w-9 h-9 rounded-full bg-red-600 text-white font-black flex items-center justify-center flex-shrink-0">{i + 1}</div>
                <div>
                  <h3 className="font-black text-gray-900">{s.t}</h3>
                  <p className="text-gray-600 text-sm mt-1">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case study */}
      <section className="px-4 py-10 bg-slate-50">
        <div className="max-w-4xl mx-auto bg-white rounded-3xl p-8 border border-gray-100 shadow-sm" data-testid="agency-case-study">
          <div className="flex items-center gap-2 text-xs font-bold text-red-500 uppercase tracking-widest mb-3"><Quote size={14} /> Client Case Study</div>
          <p className="text-gray-700 leading-relaxed text-lg italic">{page.caseStudy}</p>
          <Link to="/growth-story" className="inline-flex items-center gap-1 mt-4 text-sm font-black text-red-600 hover:underline">See real client dashboards & video testimonials <ArrowRight size={14} /></Link>
        </div>
      </section>

      {/* Pricing */}
      <section className="px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-4">Pricing</h2>
          <p className="text-gray-600 leading-relaxed mb-5">
            BrandBorn works on transparent, platform-wise packages — single-platform onboarding, multi-platform bundles, and onboarding + growth management retainers. Pricing depends on your category, platform mix and catalog size, so every brand gets a custom quote after a <strong className="text-gray-900">free eligibility check</strong>. There are no hidden charges, and quick commerce (Blinkit/Zepto/Instamart) trios are bundled at preferential rates.
          </p>
          <a href="tel:+919464645491" className="inline-flex items-center gap-2 px-7 py-3.5 bg-black text-white rounded-xl font-black text-xs uppercase tracking-widest hover:-translate-y-0.5 transition-transform"><Phone size={14} /> Get a Custom Quote — +91-9464645491</a>
        </div>
      </section>

      {/* Why choose */}
      <section className="px-4 py-12 bg-gray-950">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black text-white mb-8">Why Choose BrandBorn?</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {WHY.map((w, i) => (
              <div key={i} className="flex items-start gap-2 text-gray-200 text-sm font-medium bg-white/5 border border-white/10 rounded-xl p-4">
                <CheckCircle size={16} className="text-green-400 mt-0.5 flex-shrink-0" /> {w}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4 mt-6 text-xs text-gray-400 font-bold">
            <Newspaper size={14} /> As featured in: <span className="text-white">The Times of India</span> • <span className="text-white">The Economic Times</span>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="px-4 py-12 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-8">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {page.faqs.map((f, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-testid={`agency-faq-${i}`}>
                <h3 className="font-black text-gray-900 mb-2">{f.q}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal links */}
      <section className="px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl font-black text-gray-900 mb-5">Related Services</h2>
          <div className="grid sm:grid-cols-2 gap-3 mb-6">
            {related.map((r) => (
              <Link key={r.slug} to={`/${r.slug}`} className="flex items-center justify-between gap-2 p-4 bg-slate-50 hover:bg-red-50 border border-gray-100 hover:border-red-200 rounded-xl font-bold text-sm text-gray-800 transition-colors" data-testid={`agency-related-${r.slug}`}>
                {r.kw} <ArrowRight size={15} className="text-red-500 flex-shrink-0" />
              </Link>
            ))}
          </div>
          {software.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {software.map((s) => (
                <Link key={s.slug} to={`/software/${s.slug}`} className="px-4 py-2 bg-blue-50 text-blue-700 border border-blue-100 rounded-full text-xs font-bold hover:bg-blue-100 transition-colors">{s.kw}</Link>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-3 text-sm font-bold">
            <Link to="/services" className="text-blue-600 hover:underline">All Services →</Link>
            <Link to="/software" className="text-blue-600 hover:underline">AI Software →</Link>
            <Link to="/knowledge" className="text-blue-600 hover:underline">Knowledge Base →</Link>
            <Link to="/growth-story" className="text-blue-600 hover:underline">Case Studies →</Link>
            <Link to="/blog" className="text-blue-600 hover:underline">Blog →</Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 py-14 bg-gradient-to-r from-red-600 to-orange-500">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-black text-white mb-4">Launch Your Brand with India's #1 Onboarding Agency</h2>
          <p className="text-white/90 mb-6">Free eligibility check • 14-day standard launch • 98% first-time approval</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:+919464645491" className="px-8 py-4 bg-white text-red-600 rounded-2xl font-black text-sm uppercase tracking-wider hover:-translate-y-0.5 transition-transform shadow-xl">Call +91-9464645491</a>
            <a href="https://wa.me/919464645491?text=Hi%20BrandBorn!%20I%20want%20to%20onboard%20my%20brand." target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-black text-white rounded-2xl font-black text-sm uppercase tracking-wider hover:-translate-y-0.5 transition-transform shadow-xl">WhatsApp Now</a>
          </div>
        </div>
      </section>
    </div>
  );
}
